import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { firstValueFrom } from 'rxjs';
import {
  ChatMessage,
  ChatSession,
  Conversation,
  FeedbackCategory,
  FeedbackRating,
  MessageAttachment,
} from './ai.models';
import { PebblApiService, mapUsage } from './pebbl-api.service';
import { uuidV4 } from './uuid.util';
import { normalizePebblComponent } from './product-cards.util';
import { ProductCardsService } from './product-cards.service';

/**
 * Owns the live streaming state for every conversation, keyed by UUID. Streams run
 * here — decoupled from any view — so a turn keeps filling its buffer when the user
 * switches away (and is rejoined intact on return), and so several conversations can
 * stream in parallel. ChatViewComponent projects the active session; AiComponent
 * listens on the lifecycle subjects to keep the sidebar in sync.
 *
 * Change propagation stays in the existing manual-CD style: methods mutate a session
 * in place and then emit its id on {@link changes$}; consumers call detectChanges()
 * themselves for the affected id (the SSE reads aren't reliably inside Angular's zone,
 * which is why the previous component drove change detection manually too).
 */
@Injectable({ providedIn: 'root' })
export class ChatStreamStore {
  /* ################### Variable Declaration Start ################### */
  private sessions = new Map<string, ChatSession>();

  /** Emits the conversation id whose session just changed (per-token + REST loads). */
  readonly changes$ = new Subject<string>();
  /** A conversation was minted on the first turn of a new chat (sidebar + activeId). */
  readonly sessionCreated$ = new Subject<Conversation>();
  /** A turn finished — persisted server-side; the shell bumps/renames/reconciles. */
  readonly turnCompleted$ = new Subject<{ id: string; userText: string }>();

  /** Feedback requests in flight, keyed by (globally-unique) assistant message uuid. */
  private feedbackBusy = new Set<string>();
  /* ################### Variable Declaration End ################### */

  /* ################### Constructor Declaration Start ################### */
  constructor(
    private api: PebblApiService,
    private productCards: ProductCardsService,
  ) {}
  /* ################### Constructor Declaration End ################### */

  /************************************
   * Session access start
   ************************************/
  getSession(id: string): ChatSession | undefined {
    return this.sessions.get(id);
  }

  /** Look up an existing session, or insert an idle empty one for `id`. */
  getOrCreateSession(id: string): ChatSession {
    let session = this.sessions.get(id);
    if (!session) {
      session = this.blankSession(id);
      this.sessions.set(id, session);
    }
    return session;
  }

  /** The ids of every conversation with a stream currently in flight. */
  streamingIds(): Set<string> {
    const ids = new Set<string>();
    for (const s of this.sessions.values()) if (s.streaming) ids.add(s.id);
    return ids;
  }

  /** Abort and drop a session (used when its conversation is deleted). */
  dropSession(id: string): void {
    const session = this.sessions.get(id);
    if (!session) return;
    session.controller?.abort();
    this.sessions.delete(id);
  }

  private blankSession(id: string): ChatSession {
    return {
      id,
      messages: [],
      input: '',
      status: null,
      streaming: false,
      controller: null,
      msgCursor: null,
      loadingMessages: false,
      loaded: false,
      selfCreated: false,
    };
  }
  /************************************
   * Session access end
   ************************************/

  /************************************
   * Message loading start
   ************************************/
  /**
   * Fetch the opening page of history for `id`. No-op if the session is streaming or
   * already loaded — the live buffer is authoritative, so this is the rejoin guard
   * that stops a chat switch from clobbering an in-flight (or already-populated) view.
   */
  loadMessages(id: string): void {
    const session = this.getOrCreateSession(id);
    if (session.streaming || session.loaded) return;
    session.loadingMessages = true;
    session.msgCursor = null;
    this.changes$.next(id);
    this.api.listMessages(id, { limit: 50 }).subscribe({
      next: (page) => {
        const s = this.sessions.get(id);
        if (!s || s.streaming) return;
        s.messages = page.items;
        s.msgCursor = page.nextCursor;
        s.loadingMessages = false;
        s.loaded = true;
        this.changes$.next(id);
      },
      error: () => {
        const s = this.sessions.get(id);
        if (!s || s.streaming) return;
        s.messages = [];
        s.msgCursor = null;
        s.loadingMessages = false;
        s.loaded = false; // leave unloaded so revisiting the conversation retries
        this.changes$.next(id);
      },
    });
  }

  loadOlderMessages(id: string): void {
    const session = this.sessions.get(id);
    if (!session || session.msgCursor == null) return;
    this.api.listMessages(id, { limit: 50, cursor: session.msgCursor }).subscribe({
      next: (page) => {
        const s = this.sessions.get(id);
        if (!s) return;
        s.messages = [...page.items, ...s.messages];
        s.msgCursor = page.nextCursor;
        this.changes$.next(id);
      },
      error: () => undefined,
    });
  }
  /************************************
   * Message loading end
   ************************************/

  /************************************
   * Composer / streaming start
   ************************************/
  /** Persist a conversation's composer draft so it survives switching away. */
  setDraft(id: string, text: string): void {
    const session = this.sessions.get(id);
    if (session) session.input = text;
  }

  /**
   * Send a turn. `id` is null for a brand-new chat — a conversation is minted first
   * (emitting sessionCreated$) and its UUID returned. Blocked per-thread: a second
   * turn to a conversation already streaming is ignored, but other conversations are
   * free to stream in parallel. Returns the resolved conversation id, or null if a
   * new chat failed to initialise (the caller renders the inline error).
   *
   * @param attachments Images already uploaded and confirmed for this turn. They are
   *   shown on the user bubble and their ids are sent with the request.
   * @param reservedId Conversation id a new chat already committed to — the composer
   *   mints one client-side when the user attaches an image, because attachments are
   *   authorised against a specific conversation before the first turn exists. The chat
   *   endpoint creates the conversation under that id, so `initConversation` is skipped.
   */
  async runQuery(
    id: string | null,
    text: string,
    model: string,
    thinkingLevel: string,
    attachments: MessageAttachment[] = [],
    reservedId?: string,
  ): Promise<string | null> {
    const trimmed = (text || '').trim();
    // An image-only turn is valid — the model can be asked to describe an attachment.
    if (!trimmed && attachments.length === 0) return id;

    let convId = id;
    if (!convId) {
      if (reservedId) {
        convId = reservedId;
        this.adoptNewSession(convId, new Date().toISOString());
      } else {
        try {
          const created = await firstValueFrom(this.api.initConversation());
          convId = created.id;
          this.adoptNewSession(convId, created.createdAt);
        } catch {
          return null;
        }
      }
    }

    const session = this.getOrCreateSession(convId);
    // Per-thread lock: one turn at a time within a conversation.
    if (session.streaming) return convId;

    session.messages = [
      ...session.messages,
      { role: 'user', text: trimmed, timeline: [], components: [], attachments, createdAt: new Date().toISOString() },
    ];
    this.changes$.next(convId);

    await this.streamTurn(
      session,
      trimmed,
      model,
      thinkingLevel,
      attachments.map((a) => a.attachmentUuid),
    );
    return convId;
  }

  /** Register the session for a just-created conversation and announce it to the shell. */
  private adoptNewSession(convId: string, createdAt: string): void {
    const session = this.blankSession(convId);
    session.selfCreated = true;
    session.loaded = true; // a fresh chat has no server history to fetch
    this.sessions.set(convId, session);
    this.sessionCreated$.next({
      id: convId,
      title: 'New chat',
      messageCount: 0,
      updatedAt: createdAt,
    });
    this.changes$.next(convId);
  }

  /** Re-run the prompt that produced the last answer, replacing it (no new user bubble). */
  regenerate(id: string, model: string, thinkingLevel: string): void {
    const session = this.sessions.get(id);
    if (!session || session.streaming) return;
    const index = session.messages.length - 1;
    const last = session.messages[index];
    if (!last || last.role !== 'assistant') return;
    const prev = session.messages[index - 1];
    if (!prev || prev.role !== 'user') return;
    session.messages = session.messages.slice(0, index);
    this.changes$.next(id);
    void this.streamTurn(session, prev.text, model, thinkingLevel, [], true);
  }

  /** Stop the in-flight turn for exactly this conversation. */
  stop(id: string): void {
    this.sessions.get(id)?.controller?.abort();
  }

  /**
   * Stream one assistant turn into `session`. Assumes the user turn (if any) is set.
   * `regenerate` re-runs the original question under a fresh message uuid so the
   * backend replaces the last answer instead of appending a new user turn.
   */
  private async streamTurn(
    session: ChatSession,
    userText: string,
    model: string,
    thinkingLevel: string,
    attachmentUuids: string[] = [],
    regenerate = false,
  ): Promise<void> {
    const turnMessageId = uuidV4();
    const convId = session.id;

    const assistant: ChatMessage = {
      role: 'assistant',
      text: '',
      timeline: [],
      components: [],
      streaming: true,
      createdAt: new Date().toISOString(),
    };

    const controller = new AbortController();
    session.controller = controller;
    session.streaming = true;
    session.status = null;
    session.messages = [...session.messages, assistant];
    this.changes$.next(convId);

    try {
      for await (const event of this.api.streamChat({
        conversationUuid: convId,
        messageUuid: turnMessageId,
        message: userText,
        model,
        thinkingLevel,
        attachmentUuids,
        regenerate,
        signal: controller.signal,
      })) {
        switch (event.type) {
          case 'update':
            session.status = event.data.message;
            break;
          case 'reasoning':
            session.status = 'Thinking…';
            break;
          case 'tool_start':
            session.status = event.data.label || 'Working…';
            break;
          case 'answer':
            session.status = null;
            assistant.text += event.data.text;
            break;
          case 'component': {
            // Unknown or malformed components are dropped, never coerced.
            const component = normalizePebblComponent(event.data);
            if (component) assistant.components = [...assistant.components, component];
            // Cards arrive before any text: start resolving now so imagery is ready by
            // the time the opening paragraph closes. The strip reuses this request.
            if (component?.type === 'product_cards') this.productCards.prefetch(component.skus);
            break;
          }
          case 'usage':
            assistant.usage = mapUsage(event.data as any);
            break;
          case 'complete':
            if (event.data?.message_uuid) assistant.uuid = event.data.message_uuid;
            break;
          // tool_end / api_call — received and persisted server-side, but not rendered.
          default:
            break;
        }
        this.changes$.next(convId);
      }
    } catch (err) {
      // A user-initiated Stop is not an error — keep whatever streamed in place.
      const aborted = err instanceof DOMException && err.name === 'AbortError';
      if (!aborted && !assistant.text) assistant.error = this.errorMessage(err);
    } finally {
      assistant.streaming = false;
      session.controller = null;
      session.streaming = false;
      session.status = null;
      this.turnCompleted$.next({ id: convId, userText });
      this.changes$.next(convId);
    }
  }
  /************************************
   * Composer / streaming end
   ************************************/

  /************************************
   * Feedback (thumbs up / down) start
   ************************************/
  vote(id: string, msg: ChatMessage, choice: FeedbackRating): void {
    if (!msg.uuid || this.feedbackBusy.has(msg.uuid)) return;
    // Re-clicking the active thumb clears the vote (ChatGPT behaviour).
    const target: FeedbackRating | null = msg.feedback === choice ? null : choice;
    const previous = msg.feedback ?? null;
    const messageId = msg.uuid;
    this.feedbackBusy.add(messageId);
    msg.feedback = target; // optimistic
    this.changes$.next(id);
    const request$ =
      target === null
        ? this.api.clearFeedback(id, messageId)
        : this.api.setFeedback(id, messageId, target);
    request$.subscribe({
      error: () => {
        msg.feedback = previous; // revert on failure
        this.feedbackBusy.delete(messageId);
        this.changes$.next(id);
      },
      complete: () => {
        this.feedbackBusy.delete(messageId);
      },
    });
  }

  /**
   * Submit thumbs-down feedback from the popup. Two things happen: the message rating
   * is persisted ('down') so the thumb stays highlighted (and survives a reload), and
   * a standalone feedback item (category + message) is recorded for the review section.
   * The rating drives the thumb, so its failure reverts the optimistic highlight; the
   * feedback item is recorded independently.
   */
  submitFeedback(id: string, msg: ChatMessage, category: FeedbackCategory, message: string): void {
    if (!msg.uuid || this.feedbackBusy.has(msg.uuid)) return;
    const previous = msg.feedback ?? null;
    const messageId = msg.uuid;
    this.feedbackBusy.add(messageId);
    msg.feedback = 'down'; // optimistic
    this.changes$.next(id);
    // Record the review item (independent of the thumb state).
    this.api.createFeedback(category, message).subscribe({ error: () => undefined });
    // Persist the rating — this is what keeps the thumb-down lit.
    this.api.setFeedback(id, messageId, 'down').subscribe({
      error: () => {
        msg.feedback = previous; // revert on failure
        this.feedbackBusy.delete(messageId);
        this.changes$.next(id);
      },
      complete: () => {
        this.feedbackBusy.delete(messageId);
      },
    });
  }
  /************************************
   * Feedback (thumbs up / down) end
   ************************************/

  /************************************
   * Internal helpers start
   ************************************/
  private errorMessage(err: unknown): string {
    if (err instanceof Error) return err.message;
    return String(err);
  }
  /************************************
   * Internal helpers end
   ************************************/
}
